import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEOSchema from '../components/SEOSchema';
import Header from '../components/Header';
import Footer from '../components/Footer';
import ProgramFramework from '../components/ProgramFramework';
import { 
  Baby, 
  Heart, 
  CheckCircle, 
  ArrowRight, 
  Apple,
  Shield,
  Calendar,
  Sparkles,
  ChevronDown,
  Stethoscope,
  Clock
} from 'lucide-react';

const PregnancyNutritionPage = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const trimesterFocus = [
    {
      trimester: "First Trimester",
      weeks: "Weeks 1-12",
      color: "emerald",
      focus: [
        "Folic acid and neural tube development",
        "Managing morning sickness through diet",
        "Essential vitamins and minerals foundation",
        "Foods to avoid and safe alternatives"
      ]
    },
    {
      trimester: "Second Trimester",
      weeks: "Weeks 13-26",
      color: "blue",
      focus: [
        "Increased protein for fetal growth",
        "Calcium and vitamin D for bone development",
        "Iron-rich foods to prevent anemia",
        "Healthy weight gain strategies"
      ]
    },
    {
      trimester: "Third Trimester",
      weeks: "Weeks 27-40",
      color: "pink",
      focus: [
        "DHA and omega-3 for brain development",
        "Preparing body for delivery",
        "Managing gestational diabetes risk",
        "Energy-sustaining nutrition"
      ]
    }
  ];

  const benefits = [
    {
      icon: <Stethoscope className="w-6 h-6" />,
      title: "Gynecologist Supervised",
      description: "Our MS-qualified gynecologist reviews every pregnancy nutrition plan, ensuring medical safety for both mother and baby."
    },
    {
      icon: <Apple className="w-6 h-6" />,
      title: "Trimester-Specific Plans",
      description: "Nutrition requirements change throughout pregnancy. Our plans adapt to each trimester's unique needs."
    },
    {
      icon: <Shield className="w-6 h-6" />,
      title: "Safe Food Guidelines",
      description: "Clear guidance on foods to embrace and avoid, with safe alternatives that satisfy cravings."
    },
    {
      icon: <Heart className="w-6 h-6" />,
      title: "Mother and Baby Wellness",
      description: "Balanced approach that supports maternal health while optimizing fetal development."
    },
    {
      icon: <Calendar className="w-6 h-6" />,
      title: "Weekly Plan Updates",
      description: "Your nutrition plan evolves weekly based on your progress, symptoms, and changing needs."
    },
    {
      icon: <Clock className="w-6 h-6" />,
      title: "Postpartum Preparation",
      description: "Nutrition strategies that prepare your body for recovery and breastfeeding after delivery."
    }
  ];

  const whatWeAddress = [
    "Morning sickness and food aversions",
    "Healthy pregnancy weight gain",
    "Gestational diabetes prevention",
    "Iron deficiency and anemia",
    "Constipation and digestive issues",
    "Food cravings management",
    "Vegetarian and vegan pregnancy nutrition",
    "High-risk pregnancy nutritional support"
  ];

  const faqs = [
    {
      question: "What is included in a pregnancy diet plan?",
      answer: "Our pregnancy diet plan includes trimester-specific meal plans designed by our medical team, weekly nutritional guidance, supplement recommendations reviewed by our gynecologist, safe food lists with alternatives, recipes using everyday ingredients, and regular check-ins to adjust plans based on your pregnancy progress and any symptoms you experience."
    },
    {
      question: "Is your pregnancy nutrition program supervised by doctors?",
      answer: "Yes, every pregnancy nutrition program at TCUBE FITNESS is supervised by our medical team. Our Medical Lead, Dr. Pooja Sree (MBBS, DNB General Medicine), designs personalized plans with input from our MS-qualified gynecologist. This dual-doctor supervision ensures both nutritional adequacy and medical safety throughout your pregnancy journey."
    },
    {
      question: "Can you help with gestational diabetes through diet?",
      answer: "Absolutely. Our team has extensive experience creating pregnancy nutrition plans that help manage and prevent gestational diabetes. We focus on balanced blood sugar through strategic meal timing, appropriate carbohydrate choices, fiber-rich foods, and regular monitoring. All recommendations are made in coordination with your healthcare provider."
    },
    {
      question: "What if I have severe morning sickness or food aversions?",
      answer: "Our pregnancy nutrition plans are designed to be flexible and accommodating. We work around your food aversions, suggesting alternatives that provide similar nutrients. For severe morning sickness, we recommend small, frequent meals, focus on tolerable foods that still meet nutritional needs, and adjust plans as your symptoms evolve throughout the pregnancy."
    },
    {
      question: "Do you offer vegetarian pregnancy diet plans?",
      answer: "Yes, we specialize in both vegetarian and non-vegetarian pregnancy nutrition plans. Our team ensures vegetarian mothers receive adequate protein, iron, B12, and other nutrients critical for pregnancy through plant-based sources and appropriate supplementation when needed. We use everyday Indian foods that are familiar and easy to prepare."
    },
    {
      question: "How is your program different from generic pregnancy diet advice?",
      answer: "Unlike generic advice found online, our program provides personalized nutrition based on your specific health profile, medical history, and pregnancy progression. You receive direct access to medical professionals who understand pregnancy complications, can coordinate with your OB-GYN, and adjust plans based on clinical markers. This medical oversight makes our program significantly safer and more effective than one-size-fits-all approaches."
    }
  ];

  return (
    <>
      <Helmet>
        <title>Pregnancy Nutrition Program | Trimester Diet &amp; Prenatal Nutrition | TCUBE FITNESS</title>
        <meta name="description" content="Expert pregnancy nutrition plans supervised by gynecologists and doctors. Trimester-specific diet plans, gestational diabetes support, and safe food guidance for healthy pregnancy." />
        <meta name="keywords" content="pregnancy diet plan, prenatal nutrition, pregnancy nutrition program, gestational diabetes diet, pregnancy food guide, trimester diet plan, pregnancy meal plan, healthy pregnancy diet, gynecologist approved diet" />
        <link rel="canonical" href="https://tcubefitness.com/pregnancy-nutrition" />
        <meta property="og:title" content="Pregnancy Diet Plan | Doctor-Supervised Prenatal Nutrition | TCUBE FITNESS" />
        <meta property="og:description" content="Gynecologist-supervised pregnancy nutrition. Trimester-specific meal plans for a healthy pregnancy journey." />
        <meta property="og:url" content="https://tcubefitness.com/pregnancy-nutrition" />
        <meta property="og:type" content="website" />
      </Helmet>

      <SEOSchema
        type="service"
        name="Pregnancy Nutrition Program"
        description="Gynecologist-supervised prenatal nutrition with trimester-specific meal plans, gestational diabetes support and safe-food guidance."
        url="/pregnancy-nutrition"
        breadcrumbs={[
          { name: 'Home', path: '/' },
          { name: 'Pregnancy Nutrition', path: '/pregnancy-nutrition' },
        ]}
      />

      <div className="min-h-screen bg-slate-950">
        <Header />
        
        {/* Hero Section */}
        <section className="pt-28 pb-20 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-600/20 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-pink-600/20 rounded-full blur-3xl"></div>
          </div>
          
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full text-emerald-400 text-sm font-medium mb-6">
                <Baby className="w-4 h-4" />
                Doctor-Supervised Prenatal Care
              </div>
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                Pregnancy Diet Plans for Healthy Mothers and Babies
              </h1>
              
              <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-3xl mx-auto leading-relaxed">
                Nourish your pregnancy with expert-designed nutrition plans. Our gynecologist-supervised programs provide trimester-specific guidance, helping you and your baby thrive throughout this beautiful journey.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-gradient-to-r from-emerald-600 to-green-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:scale-105 shadow-lg shadow-emerald-500/25"
                  data-testid="pregnancy-cta"
                >
                  Start Your Pregnancy Plan
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <a 
                  href="https://wa.me/919515901777"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-slate-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-800"
                >
                  Free Consultation
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-3xl mx-auto">
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-emerald-400 font-bold text-lg">MS</p>
                  <p className="text-slate-500 text-sm">Gynecologist</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-blue-400 font-bold text-lg">MBBS</p>
                  <p className="text-slate-500 text-sm">Medical Lead</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-pink-400 font-bold text-lg">Weekly</p>
                  <p className="text-slate-500 text-sm">Plan Updates</p>
                </div>
                <div className="bg-slate-900/50 rounded-xl p-4 border border-slate-800">
                  <p className="text-orange-400 font-bold text-lg">100%</p>
                  <p className="text-slate-500 text-sm">Online Support</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Gestational diabetes callout */}
        <section className="py-8 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto bg-blue-500/5 border border-blue-500/20 rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="pregnancy-gdm-callout">
              <div className="flex-1">
                <h2 className="text-lg font-bold text-white mb-1">Managing high blood sugar in pregnancy?</h2>
                <p className="text-slate-400 text-sm leading-relaxed">
                  Read our doctor-informed guide to a gestational diabetes diet, with practical Indian meal guidance and why medical monitoring matters.
                </p>
              </div>
              <Link to="/blog/gestational-diabetes-diet-pregnancy" className="inline-flex items-center justify-center gap-2 bg-blue-600 hover:bg-blue-500 text-white px-5 py-2.5 rounded-full font-semibold text-sm whitespace-nowrap flex-shrink-0" data-testid="pregnancy-gdm-link">
                Gestational Diabetes Diet <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </Link>
            </div>
          </div>
        </section>

        {/* Introduction Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6 text-center">
                Why <span className="text-emerald-400">Pregnancy Nutrition</span> Matters
              </h2>
              
              <div className="bg-slate-900/50 rounded-3xl p-8 border border-slate-800 mb-12">
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  Pregnancy is one of the most nutritionally demanding times in a woman's life. What you eat directly impacts your baby's development, your energy levels, and your body's ability to handle the physical demands of pregnancy and childbirth.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed mb-6">
                  At TCUBE FITNESS, we understand that every pregnancy is unique. That is why our <Link to="/#team" className="text-emerald-400 hover:underline">MS-qualified gynecologist</Link> and <Link to="/doctor-led-fitness" className="text-blue-400 hover:underline">Medical Lead</Link> work together to create pregnancy nutrition plans tailored to your specific needs, health history, and dietary preferences.
                </p>
                <p className="text-slate-300 text-lg leading-relaxed">
                  Whether you are planning for pregnancy, currently expecting, or navigating a high-risk pregnancy, our medically supervised approach ensures you receive safe, evidence-based nutritional guidance throughout your journey to motherhood.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Trimester-Specific Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                <span className="text-emerald-400">Trimester-Specific</span> Nutrition Plans
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Your nutritional needs evolve throughout pregnancy. Our plans adapt to each stage.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {trimesterFocus.map((item, idx) => (
                <div 
                  key={idx}
                  className={`bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-${item.color}-500/30 transition-all`}
                >
                  <div className={`inline-flex items-center gap-2 px-3 py-1 bg-${item.color}-500/10 rounded-full text-${item.color}-400 text-sm font-medium mb-4`}>
                    {item.weeks}
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">{item.trimester}</h3>
                  <ul className="space-y-3">
                    {item.focus.map((point, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle className={`w-5 h-5 text-${item.color}-400 flex-shrink-0 mt-0.5`} />
                        <span className="text-slate-400 text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Why Choose Our <span className="text-pink-400">Pregnancy Program</span>?
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Medical expertise combined with personalized care for expecting mothers
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {benefits.map((benefit, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl p-6 border border-slate-800 hover:border-emerald-500/30 transition-all"
                >
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center text-emerald-400 mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold text-white mb-3">{benefit.title}</h3>
                  <p className="text-slate-400 leading-relaxed">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* What We Address Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                  Pregnancy Challenges We <span className="text-blue-400">Help With</span>
                </h2>
                <p className="text-slate-400">
                  Our medical team has experience supporting mothers through various pregnancy-related nutritional challenges
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {whatWeAddress.map((item, idx) => (
                  <div 
                    key={idx}
                    className="flex items-center gap-3 bg-slate-900/50 rounded-xl p-4 border border-slate-800"
                  >
                    <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
                    <span className="text-slate-300">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Post-Pregnancy Section */}
        <section className="py-20 relative">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <div className="bg-gradient-to-r from-pink-900/30 via-emerald-900/30 to-pink-900/30 rounded-3xl p-8 border border-pink-500/20">
                <div className="text-center mb-8">
                  <h2 className="text-3xl font-bold text-white mb-4">
                    Postpartum Nutrition Support
                  </h2>
                  <p className="text-slate-300">
                    Your nutritional needs do not end at delivery. We provide continued support.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800">
                    <h3 className="text-lg font-semibold text-white mb-2">Recovery Nutrition</h3>
                    <p className="text-slate-400 text-sm">Nutrient-rich plans to help your body heal and regain strength after delivery.</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800">
                    <h3 className="text-lg font-semibold text-white mb-2">Breastfeeding Support</h3>
                    <p className="text-slate-400 text-sm">Nutrition optimized for milk production and quality while maintaining your energy.</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800">
                    <h3 className="text-lg font-semibold text-white mb-2">Gradual Weight Management</h3>
                    <p className="text-slate-400 text-sm">Safe, sustainable approach to postpartum weight loss without compromising milk supply.</p>
                  </div>
                  <div className="bg-slate-900/50 rounded-xl p-5 border border-slate-800">
                    <h3 className="text-lg font-semibold text-white mb-2">Energy Restoration</h3>
                    <p className="text-slate-400 text-sm">Combat new-mother fatigue with strategic nutrition that fuels your demanding days.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 relative bg-slate-900/30">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
                Pregnancy Nutrition <span className="text-emerald-400">FAQs</span>
              </h2>
              <p className="text-slate-400 max-w-2xl mx-auto">
                Common questions about our pregnancy diet programs
              </p>
            </div>

            <div className="max-w-3xl mx-auto space-y-4">
              {faqs.map((faq, idx) => (
                <div 
                  key={idx}
                  className="bg-slate-900/50 rounded-2xl border border-slate-800 overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                    className="w-full flex items-center justify-between p-5 text-left"
                  >
                    <span className="text-white font-semibold pr-4">{faq.question}</span>
                    <ChevronDown className={`w-5 h-5 text-slate-500 transition-transform ${openFaq === idx ? 'rotate-180 text-emerald-400' : ''}`} />
                  </button>
                  <div className={`overflow-hidden transition-all ${openFaq === idx ? 'max-h-96' : 'max-h-0'}`}>
                    <p className="px-5 pb-5 text-slate-400 leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 relative">
          <div className="absolute inset-0 bg-gradient-to-r from-emerald-900/50 via-green-900/50 to-emerald-900/50"></div>
          <div className="container mx-auto px-4 sm:px-6 relative z-10">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                Nourish Your Pregnancy Journey
              </h2>
              <p className="text-slate-300 text-lg mb-8">
                Get personalized, gynecologist-supervised nutrition support for a healthy pregnancy.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link
                  to="/submit-details"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-white text-slate-900 px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-slate-100"
                >
                  Start Your Pregnancy Plan
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/packages"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 border-2 border-white/30 text-white px-8 py-4 rounded-full font-bold text-lg transition-all hover:bg-white/10"
                >
                  View Packages
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Internal Links Section */}
        <section className="py-12 border-t border-slate-800">
          <div className="container mx-auto px-4 sm:px-6">
            <div className="max-w-4xl mx-auto">
              <h3 className="text-lg font-semibold text-white mb-4">Related Programs</h3>
              <div className="flex flex-wrap gap-3">
                <Link to="/fertility-nutrition" className="text-slate-400 hover:text-pink-400 transition-colors">Fertility Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/doctor-led-fitness" className="text-slate-400 hover:text-blue-400 transition-colors">Doctor-Led Fitness</Link>
                <span className="text-slate-700">|</span>
                <Link to="/medical-nutrition-therapy" className="text-slate-400 hover:text-emerald-400 transition-colors">Clinical Nutrition</Link>
                <span className="text-slate-700">|</span>
                <Link to="/our-team" className="text-slate-400 hover:text-orange-400 transition-colors">Meet Our Team</Link>
                <span className="text-slate-700">|</span>
                <Link to="/packages" className="text-slate-400 hover:text-cyan-400 transition-colors">All Programs</Link>
              </div>
            </div>
          </div>
        </section>

        <ProgramFramework slug="pregnancy-nutrition" />
        <Footer />
      </div>
    </>
  );
};

export default PregnancyNutritionPage;
